import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Users, Wallet, TrendingUp, Loader2, Star, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const last12Months = () => {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 11; i >= 0; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({
      key: m.toISOString().slice(0, 7),
      label: m.toLocaleDateString("en-IN", { month: "short" }) + " " + String(m.getFullYear()).slice(2),
    });
  }
  return out;
};

const tooltipStyle = {
  contentStyle: { background: "#0A2949", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "IBM Plex Mono, monospace", fontSize: 12 },
  labelStyle: { color: "#94A3B8" },
  itemStyle: { color: "#fff" },
};

export const AdminDashboard = ({ client, api, goTab, isAdmin }) => {
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`${api}/qv2/dashboard`);
      setStats(data);
    } catch {
      toast.error("Could not load dashboard");
    }
  }, [client, api]);

  useEffect(() => {
    load();
  }, [load]);

  if (!stats) {
    return (
      <div className="flex justify-center py-24" data-testid="dashboard-loading">
        <Loader2 size={32} className="animate-spin text-smoke" />
      </div>
    );
  }

  const months = last12Months();
  const quoteSeries = months.map((m) => ({ name: m.label, count: stats.monthly.find((x) => x.month === m.key)?.count || 0 }));
  const bookedSeries = months.map((m) => {
    const hit = stats.monthly_booked.find((x) => x.month === m.key);
    return { name: m.label, booked: hit?.booked || 0, margin: hit?.margin || 0 };
  });

  const tiles = [
    { id: "tile-quotations", label: "Total Quotations", value: stats.total_quotations, icon: FileText, tab: "quotes" },
    { id: "tile-customers", label: "Customers · Prime", value: `${stats.customers} · ${stats.prime_customers}`, icon: Users, tab: "customers" },
    { id: "tile-order-booked", label: "Order Booked", value: fmt(stats.order_booked), icon: Wallet, tab: "orders" },
    ...(isAdmin ? [{ id: "tile-margin", label: "Total Margin", value: fmt(stats.total_margin), icon: TrendingUp, tab: "quotes" }] : []),
  ];

  return (
    <div data-testid="quote-dashboard">
      <div className={`grid grid-cols-2 ${tiles.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-px bg-white/10 border border-white/10 mb-6`}>
        {tiles.map((t) => (
          <button key={t.id} data-testid={t.id} onClick={() => goTab(t.tab)} className="bg-[#062B4E] p-6 md:p-7 text-left hover:bg-[#083A66] transition-colors duration-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">{t.label}</p>
                <p className="font-heading font-semibold text-3xl md:text-4xl text-white mt-2 leading-none">{t.value}</p>
                <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-smoke/70">Tap for details ↓</p>
              </div>
              <t.icon size={20} className="text-smoke/60" strokeWidth={1.5} />
            </div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-steel border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Trend</p>
              <p className="font-heading font-semibold uppercase tracking-wide text-lg text-white mt-1">Quotations · month-wise</p>
            </div>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke/70">last 12 months</p>
          </div>
          <div data-testid="monthly-trend" className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quoteSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.15)" }} tickLine={false} interval={0} angle={-35} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} width={24} />
                <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.05)" }} />
                <Bar dataKey="count" fill="#7FB3E8" name="Quotations" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-steel border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Revenue</p>
              <p className="font-heading font-semibold uppercase tracking-wide text-lg text-white mt-1">Order Booked{isAdmin ? " · Margin" : ""}</p>
            </div>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke/70">last 12 months</p>
          </div>
          <div data-testid="booked-trend" className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bookedSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.15)" }} tickLine={false} interval={0} angle={-35} textAnchor="end" height={50} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : v)} />
                <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.05)" }} formatter={(v) => fmt(v)} />
                <Bar dataKey="booked" fill="#7FB3E8" name="Order Booked" />
                {isAdmin && <Bar dataKey="margin" fill="#F5C518" name="Margin" />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-steel border border-white/10">
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <p className="flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-white"><Star size={15} className="text-[#7FB3E8]" /> Prime Customers</p>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">2+ orders</p>
          </div>
          <table data-testid="prime-customers-table" className="w-full">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                <th className="py-2.5 px-5 font-medium">Customer</th>
                <th className="py-2.5 px-5 font-medium">Orders</th>
                <th className="py-2.5 px-5 font-medium text-right">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {stats.prime_list.length === 0 ? (
                <tr><td colSpan={3} className="py-8 text-center font-body text-sm text-smoke">No prime customers yet</td></tr>
              ) : stats.prime_list.map((p, i) => (
                <tr key={i} className="border-t border-white/10">
                  <td className="py-3 px-5 font-heading font-semibold uppercase tracking-wide text-white">{p.company}</td>
                  <td className="py-3 px-5 font-mono text-xs text-smoke">{p.orders}</td>
                  <td className="py-3 px-5 font-mono text-xs text-white text-right">{fmt(p.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-steel border border-white/10" data-testid="sales-purchase-panel">
          <div className="flex items-center gap-2 p-5 border-b border-white/10">
            <Receipt size={15} className="text-[#7FB3E8]" />
            <p className="font-heading font-semibold uppercase tracking-wider text-white">Sales · Purchase · Billing</p>
          </div>
          <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
            <div className="flex items-center justify-between border-b border-dashed border-white/10 pb-3">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">Total Sales Booked</span>
              <span data-testid="sp-sales-booked" className="font-mono text-sm text-white">{fmt(stats.order_booked)}</span>
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between border-b border-dashed border-white/10 pb-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">Material (Purchase)</span>
                <span data-testid="sp-material" className="font-mono text-sm text-white">{fmt(stats.material_purchase)}</span>
              </div>
            )}
            {isAdmin && (
              <div className="flex items-center justify-between border-b border-dashed border-white/10 pb-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">Overhead Absorbed</span>
                <span data-testid="sp-overhead" className="font-mono text-sm text-white">{fmt(stats.overhead_absorbed)}</span>
              </div>
            )}
            {isAdmin && (
              <div className="flex items-center justify-between border-b border-dashed border-white/10 pb-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">Total Margin</span>
                <span data-testid="sp-margin" className="font-mono text-sm text-green-300">{fmt(stats.total_margin)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-b border-dashed border-white/10 pb-3">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">GST Billable</span>
              <span data-testid="sp-gst" className="font-mono text-sm text-white">{fmt(stats.gst_billable)}</span>
            </div>
            <div className="flex items-center justify-between border-b border-dashed border-white/10 pb-3">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">Avg. Quote Value</span>
              <span data-testid="sp-avg" className="font-mono text-sm text-white">{fmt(stats.avg_quote_value)}</span>
            </div>
          </div>
          {isAdmin && (
            <p className="px-5 pb-5 font-mono text-[10px] tracking-[0.15em] uppercase text-smoke/60">
              Purchase = raw GI sheet cost estimated from applied margin/OH per line. GST is customer billable.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
